/**
 * Ürün ekleme diyaloğu.
 *
 * Ana yol: bağlı mağazadan URL(ler) yapıştır → backend meta/JSON-LD ile SKU + isim + görsel çeker.
 * Fallback yolu: "Sadece SKU ile ekle" → alt kısımda gizli, robot-bloklu siteler için.
 *
 * Wizard'ta da kullanılabilir (mode="return") — items array döndürür, kaydetmez.
 */
import { useState } from "react";
import { toast } from "sonner";
import {
    Loader2, Plus, Sparkles, X, Link as LinkIcon, ClipboardList,
    AlertTriangle, ChevronDown, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSizing } from "@/lib/sizing-store";

/**
 * Parse a "SKU / SKU, title" textarea block into a de-duped list of items.
 * Extra whitespace, commas, semicolons, tabs, trailing empty lines all tolerated.
 */
export function parseSkuBlock(text) {
    const seen = new Set();
    const items = [];
    for (const rawLine of (text || "").split("\n")) {
        const line = rawLine.trim();
        if (!line) continue;
        const m = line.match(/^([^,;\t]+)[,;\t]?\s*(.*)$/);
        if (!m) continue;
        const sku = m[1].trim();
        const name = (m[2] || "").trim();
        if (!sku) continue;
        const canonical = sku.toLowerCase().replace(/[^a-z0-9]+/g, "");
        if (!canonical || seen.has(canonical)) continue;
        seen.add(canonical);
        items.push({ sku, name });
    }
    return items;
}

/**
 * Parse a multi-line URL block into a de-duped array.
 * Ignores empty lines and lines that don't look like a URL.
 */
function parseUrlBlock(text) {
    const seen = new Set();
    const urls = [];
    for (const rawLine of (text || "").split("\n")) {
        const line = rawLine.trim();
        if (!line) continue;
        if (!/^https?:\/\//i.test(line)) continue;
        if (seen.has(line)) continue;
        seen.add(line); urls.push(line);
    }
    return urls;
}

export default function ProductAddDialog({
    onAdd,
    onCancel,
    mode = "active",
    submitLabel = "Ürünleri Ekle",
    testIdPrefix = "product-add",
}) {
    const { addProductsBulk, scrapeProductUrls } = useSizing();

    // URL flow state (primary)
    const [urlText, setUrlText] = useState("");
    const [scraping, setScraping] = useState(false);
    const [scrapeProgress, setScrapeProgress] = useState({ done: 0, total: 0 });
    const [results, setResults] = useState([]); // [{url, sku?, name?, image?, ok, error?, detection_method?}]

    // SKU-only fallback state
    const [skuOpen, setSkuOpen] = useState(false);
    const [pasteText, setPasteText] = useState("");

    // Shared
    const [submitting, setSubmitting] = useState(false);

    const urls = parseUrlBlock(urlText);
    const okResults = results.filter((r) => r.ok);
    const errorResults = results.filter((r) => !r.ok);
    const skuFallback = parseSkuBlock(pasteText);

    const handleScrape = async () => {
        if (urls.length === 0) { toast.error("Bir URL yapıştır"); return; }
        setScraping(true);
        setScrapeProgress({ done: 0, total: urls.length });
        setResults([]);
        try {
            const data = await scrapeProductUrls(urls);
            const rows = data?.results || [];
            setResults(rows);
            setScrapeProgress({ done: rows.length, total: urls.length });
            const foundCount = rows.filter((r) => r.ok).length;
            if (foundCount === 0) {
                toast.error("Hiçbir üründe SKU bulunamadı");
            } else if (foundCount < urls.length) {
                toast.warning(`${foundCount}/${urls.length} ürün bulundu — ${urls.length - foundCount} atlandı`);
            } else {
                toast.success(`${foundCount} ürün bulundu`);
            }
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = typeof detail === "string" && detail.length < 200 ? detail : "URL'ler çekilemedi";
            toast.error(msg);
        } finally {
            setScraping(false);
        }
    };

    const removeResult = (i) => setResults((r) => r.filter((_, idx) => idx !== i));

    // Total items about to be submitted:
    // — okResults from URL scrape (rich meta) plus
    // — skuFallback items if the "sadece SKU" section was expanded
    const finalItems = [
        ...okResults.map((r) => ({ sku: r.sku, name: r.name || "", image: r.image || "", category: r.category || "" })),
        ...(skuOpen ? skuFallback : []),
    ];
    const canSubmit = finalItems.length > 0;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        // Table-linking at add-time is intentionally removed — merchants now
        // pick a table in the wizard flow (per-product or via bulk actions),
        // which keeps the add dialog focused on ingest only.
        const payload = { items: finalItems, tableId: null };
        if (mode === "return") {
            onAdd?.(payload);
            return;
        }
        setSubmitting(true);
        try {
            const res = await addProductsBulk(payload);
            const parts = [];
            if (res?.created) parts.push(`${res.created} yeni`);
            if (res?.updated) parts.push(`${res.updated} güncellendi`);
            if (res?.skipped?.length) parts.push(`${res.skipped.length} atlandı`);
            toast.success(`Ürünler eklendi: ${parts.join(" · ") || finalItems.length}`);
            onAdd?.(res);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = typeof detail === "string" && detail.length < 200 ? detail : "Ürünler eklenemedi";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-4" data-testid={`${testIdPrefix}-dialog`}>
            {/* Info bar */}
            <div className="rounded-lg border border-info/25 bg-info-soft px-3 py-2 text-[11px] text-info flex items-center gap-2">
                <Info className="h-3.5 w-3.5 shrink-0" />
                Ürün sayfası URL'lerini yapıştır — bağlı mağazandan otomatik olarak SKU, ad ve görsel çekilir.
            </div>

            {/* Primary: URL multi-line */}
            <div>
                <label className="block">
                    <div className="text-xs font-medium text-foreground mb-1.5">
                        Ürün URL'leri <span className="text-muted-foreground font-normal">— her satıra bir URL (en fazla 100)</span>
                    </div>
                    <textarea
                        value={urlText}
                        onChange={(e) => setUrlText(e.target.value)}
                        rows={5}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-xs font-mono text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors"
                        placeholder={`https://mystore.com/products/kadin-bluz-8201\nhttps://mystore.com/products/erkek-jean-8202\nhttps://mystore.com/products/spor-ayakkabi-102`}
                        data-testid={`${testIdPrefix}-url-textarea`}
                    />
                    <div className="mt-1.5 flex items-center justify-between text-[11px] text-muted-foreground">
                        <span>{urls.length} URL algılandı</span>
                        <Button
                            onClick={handleScrape}
                            disabled={scraping || urls.length === 0}
                            className="h-7 text-[11px] bg-brand hover:bg-brand/90 text-brand-fg gap-1.5"
                            data-testid={`${testIdPrefix}-url-scrape`}
                        >
                            {scraping
                                ? <><Loader2 className="h-3 w-3 animate-spin" /> {scrapeProgress.done}/{scrapeProgress.total}</>
                                : <><Sparkles className="h-3 w-3" /> URL&apos;leri Çek</>
                            }
                        </Button>
                    </div>
                </label>
            </div>

            {/* Scrape results list */}
            {results.length > 0 && (
                <div className="space-y-2" data-testid={`${testIdPrefix}-results`}>
                    <div className="text-xs font-medium text-foreground">
                        Bulunan ürünler <span className="text-muted-foreground font-normal">— {okResults.length}/{results.length}</span>
                    </div>
                    <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1">
                        {results.map((it, i) => (
                            <div
                                key={it.url + i}
                                className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
                                    it.ok
                                        ? "border-border bg-surface"
                                        : "border-warning/30 bg-warning-soft/40"
                                }`}
                            >
                                <div className={`h-10 w-10 rounded-md overflow-hidden shrink-0 ${it.ok ? "bg-muted" : "bg-warning-soft grid place-items-center"}`}>
                                    {it.ok && it.image
                                        ? <img src={it.image} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                        : !it.ok ? <AlertTriangle className="h-4 w-4 text-warning" /> : null}
                                </div>
                                <div className="min-w-0 flex-1">
                                    {it.ok ? (
                                        <>
                                            <div className="text-xs font-medium truncate">{it.name || <em className="text-muted-foreground">İsim çekilemedi</em>}</div>
                                            <div className="text-[10px] font-mono text-muted-foreground truncate">{it.sku} · {it.url.replace(/^https?:\/\//, "")}</div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-xs font-medium truncate text-warning">{it.error || "Bulunamadı"}</div>
                                            <div className="text-[10px] font-mono text-muted-foreground truncate">{it.url}</div>
                                        </>
                                    )}
                                </div>
                                {it.ok && (
                                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground shrink-0">{it.detection_method}</span>
                                )}
                                <button
                                    onClick={() => removeResult(i)}
                                    className="text-muted-foreground hover:text-destructive p-1"
                                    aria-label="Kaldır"
                                    data-testid={`${testIdPrefix}-remove-${i}`}
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Fallback: sadece SKU (collapsible) */}
            <div className="rounded-lg border border-dashed border-border overflow-hidden">
                <button
                    type="button"
                    onClick={() => setSkuOpen((v) => !v)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/30 transition-colors"
                    data-testid={`${testIdPrefix}-sku-toggle`}
                >
                    <ClipboardList className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium">Sadece SKU ile Ekle</div>
                        <div className="text-[10px] text-muted-foreground">Site erişimi olmayan (robot bloklu) markalar için. Fotoğraf ve isim gelmez.</div>
                    </div>
                    <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform ${skuOpen ? "rotate-180" : ""}`} />
                </button>
                {skuOpen && (
                    <div className="px-3 pb-3 border-t border-dashed border-border">
                        <textarea
                            value={pasteText}
                            onChange={(e) => setPasteText(e.target.value)}
                            rows={4}
                            className="mt-3 w-full rounded-lg border border-border bg-background px-3 py-2 text-xs font-mono text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors"
                            placeholder={`PRD-8201\nPRD-8202, Slim Fit Jean\nSPK-102`}
                            data-testid={`${testIdPrefix}-paste-textarea`}
                        />
                        <div className="mt-1.5 text-[11px] text-muted-foreground">
                            {skuFallback.length} SKU algılandı — panele fotoğrafsız eklenir.
                        </div>
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between gap-2 pt-2">
                <div className="text-[11px] text-muted-foreground">
                    {finalItems.length > 0 && (
                        <>
                            <span className="font-mono num text-foreground">{finalItems.length}</span> ürün eklenecek
                            {errorResults.length > 0 && <> · <span className="text-warning">{errorResults.length} hata</span></>}
                        </>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="ghost" onClick={onCancel} disabled={submitting} className="h-9 text-xs" data-testid={`${testIdPrefix}-cancel`}>
                        İptal
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!canSubmit || submitting}
                        className="h-9 text-xs bg-brand hover:bg-brand/90 text-brand-fg"
                        data-testid={`${testIdPrefix}-submit`}
                    >
                        {submitting
                            ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Ekleniyor</>
                            : <><Plus className="h-3.5 w-3.5 mr-1.5" /> {submitLabel}</>
                        }
                    </Button>
                </div>
            </div>
        </div>
    );
}
